import { useEffect } from "react";
import { useSelector } from "react-redux";
import { Options } from "src/components/atoms/inputs/FlowioSelect/types";
import { RowKey } from "src/requests/table/types";
import { useTemplateBuilderData } from "src/store/templateBuilder/useSelectors";
import { TemplateId } from "../../../globalTypes";
import {
  getAllTemplateMetadataById,
  getAllVisibleOverviewIds,
  getDetailTemplateIdById,
  getDoesTemplateHaveNoCommonFunctions,
  getIsFetchingAll,
  getTemplateMetadataFromId,
  getTemplateMetadataFromIds,
  getTemplateNameForTemplateId,
  getTemplateNamesForGivenIds,
  getTemplatesNameAndOrderForModuleId,
} from "../selectors";
import { TemplateMetadataSTORE } from "../types";
import {
  useFetchTemplateMetadataForTemplateIds,
  useFetchTemplateMetadataFromIdCallback,
} from "./useActions";

export const useTemplateMetadataFromId = (id?: TemplateId | null) =>
  useSelector((state) => getTemplateMetadataFromId(state, id));

export const useTemplateMetadataFromIds = (ids: number[]) =>
  useSelector((state) => getTemplateMetadataFromIds(state, ids));

export const useAvailableTemplateSelect = (templateId: number) => {
  const metadata = useTemplateMetadataFromId(templateId);
  const dataSourceId = metadata?.dataSourceId || -1;

  const templateBuilder = useTemplateBuilderData(templateId);
  const ids =
    templateBuilder?.availableDetailTemplates.map((template) =>
      Number(template.value)
    ) || [];

  // Templates with metadata
  const detailedTemplates = useTemplateMetadataFromIds(ids);

  const res: Options = detailedTemplates
    .filter((detailT) => detailT?.dataSourceId === dataSourceId)
    .map((item) => ({
      label: item?.name || "",
      value: String(item?.id) || "",
      name: item?.name || "",
    }));

  return res;
};

export const useTemplateMetadataFromIdAUTOFETCH = (
  id?: TemplateId | null,
  rowKey?: RowKey
) => {
  const fetchTemplateMetadata = useFetchTemplateMetadataFromIdCallback();

  const metadata = useTemplateMetadataFromId(id);

  useEffect(() => {
    if (!metadata) fetchTemplateMetadata(id, rowKey);
  }, [id, metadata, rowKey]); // eslint-disable-line

  return metadata;
};

export const useTemplateMetadataFromIdsAUTOFETCH = (ids: number[]) => {
  const fetchTemplateIdsMetadata = useFetchTemplateMetadataForTemplateIds();

  const metadataForIds = useTemplateMetadataFromIds(ids);

  const filterOnlyFetchedIds = (
    metadata: TemplateMetadataSTORE | undefined
  ): boolean => {
    if (metadata) {
      return ids.includes(Number(metadata.id));
    }
    return false;
  };

  useEffect(() => {
    if (metadataForIds.includes(undefined)) {
      const fetchedIds = metadataForIds
        .filter(filterOnlyFetchedIds)
        .map((metadata) => metadata?.id);
      const idsToFetch = ids.filter((id) => !fetchedIds.includes(id));

      fetchTemplateIdsMetadata(idsToFetch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  return metadataForIds;
};

export const useDetailIdForTemplateId = (id: TemplateId) =>
  useSelector((state) => getDetailTemplateIdById(state, id));

export const useIsTemplateMetadataBulkFetching = () =>
  useSelector(getIsFetchingAll);

export const useVisibleOverviewTemplatesIdWithNames = (
  templateId: TemplateId
) => {
  const templatesMetadataById = useSelector(getAllTemplateMetadataById);
  const overviewTemplateIds = useSelector(getAllVisibleOverviewIds);
  const metadata = useTemplateMetadataFromId(templateId);
  const dataSourceId = metadata?.dataSourceId || -1;

  return Object.values(overviewTemplateIds)
    .filter(
      (overviewTemplateId) =>
        dataSourceId === templatesMetadataById[overviewTemplateId]?.dataSourceId
    )
    .map((overviewTemplateId) => ({
      templateId: overviewTemplateId,
      templateName: templatesMetadataById[overviewTemplateId]?.name,
    }));
};

export const useTemplateNameForTemplateId = (templateId?: TemplateId | null) =>
  useSelector((state) => getTemplateNameForTemplateId(state, templateId));

export const useTemplateNamesForGivenIds = (templateIds: TemplateId[]) =>
  useSelector((state) => getTemplateNamesForGivenIds(state, templateIds));

export const useDoesTemplateHaveNonCommonFns = (
  templateId: TemplateId | null
) => useSelector((s) => getDoesTemplateHaveNoCommonFunctions(s, templateId));

export const useTemplatesNameAndOrderForModuleId = (moduleId: number) =>
  useSelector((state) => getTemplatesNameAndOrderForModuleId(state, moduleId));
